import { useMemo } from "react";

import type { LeadPriorityScore } from "app/pages/sales-intelligence/domain/leads/types/lead-priority";
import { ScoringConfigType } from "app/pages/sales-intelligence/domain/leads/types/scoring-config";
import { filterAccountRulesByTeamMembership } from "app/pages/sales-intelligence/domain/leads/utils/filterAccountRulesByTeamMembership";
import { getEnabledLeadScoreTiers } from "app/pages/sales-intelligence/domain/leads/utils/getEnabledLeadScoreTiers";
import { hasAnyScoringRules } from "app/pages/sales-intelligence/domain/leads/utils/hasAnyScoringRules";
import { mergeScoringRules } from "app/pages/sales-intelligence/domain/leads/utils/mergeScoringRules";
import useLeadScoringConfigQuery from "app/pages/sales-intelligence/modules/lead-scoring-agent/query/queries/useLeadScoringConfigQuery";
import { useUserTeamMembership } from "app/pages/sales-intelligence/modules/teams/hooks/useUserTeamMembership";

interface UseLeadScoringFiltersDataResult {
    hasScoringRules: boolean;
    enabledLeadScoreTiers: ReadonlySet<LeadPriorityScore>;
}

export const useLeadScoringFiltersData = (enabled: boolean): UseLeadScoringFiltersDataResult => {
    const { data: userScoringConfig } = useLeadScoringConfigQuery(ScoringConfigType.User, { enabled });
    const { data: accountScoringConfig } = useLeadScoringConfigQuery(ScoringConfigType.Account, { enabled });

    const { userTeamIds, isMembershipSuccess } = useUserTeamMembership({ enabled });

    const applicableAccountRules = useMemo(() => {
        const rawAccountRules = accountScoringConfig?.rules ?? {};

        if (!isMembershipSuccess) {
            return rawAccountRules;
        }

        return filterAccountRulesByTeamMembership(rawAccountRules, userTeamIds);
    }, [accountScoringConfig?.rules, isMembershipSuccess, userTeamIds]);

    const mergedScoringRules = useMemo(
        () => mergeScoringRules(userScoringConfig?.rules ?? {}, applicableAccountRules).rules,
        [userScoringConfig?.rules, applicableAccountRules],
    );

    const hasScoringRules = hasAnyScoringRules(mergedScoringRules);
    const enabledLeadScoreTiers = useMemo(() => getEnabledLeadScoreTiers(mergedScoringRules), [mergedScoringRules]);

    return { hasScoringRules, enabledLeadScoreTiers };
};
